import { useI18n } from '../../i18n';
import type { Ring } from '../../types';
import Field from '../shared/Field';

/** The weight limits of a ring, in kg: the IFMA weight classes. One list for
 *  the Add form and the Edit modal (#661). */
export const RING_WEIGHT_LIMITS = ['51', '54', '57', '60', '63.5', '67', '71', '75', '81', '86', '91'] as const;

/** The fields of a ring as the form holds them. A select binds a string, so
 *  an empty string means "no restriction". */
export interface RingDraft {
  name: string;
  min_age_group: string;
  max_age_group: string;
  max_weight_kg: string;
}

export const EMPTY_RING_DRAFT: RingDraft = { name: '', min_age_group: '', max_age_group: '', max_weight_kg: '' };

/** The form values of a ring. `max_weight_kg` is NUMERIC: node-pg sends the
 *  string "63.50", and `String(Number(…))` gives "63.5", which matches the
 *  option of the select. */
export function ringDraftOf(ring: Pick<Ring, 'name' | 'min_age_group' | 'max_age_group' | 'max_weight_kg'>): RingDraft {
  return {
    name: ring.name,
    min_age_group: ring.min_age_group || '',
    max_age_group: ring.max_age_group || '',
    max_weight_kg: ring.max_weight_kg != null ? String(Number(ring.max_weight_kg)) : '',
  };
}

/** The body of the create and the update of a ring: an empty field goes as
 *  null (a cleared field must reach the server, #452). The server trims the
 *  name, and so does this function. */
export function ringBody(draft: RingDraft) {
  return {
    name: draft.name.trim(),
    min_age_group: draft.min_age_group || null,
    max_age_group: draft.max_age_group || null,
    max_weight_kg: draft.max_weight_kg ? Number(draft.max_weight_kg) : null,
  };
}

/** The four fields of a ring in a labelled grid. The Add form and the Edit
 *  modal of the Overview tab share it. */
export default function RingFields({ draft, onChange, ageCodes }: {
  draft: RingDraft;
  onChange: (next: RingDraft) => void;
  /** The age-group codes of the pickers (`GET /api/reference/age-groups`). */
  ageCodes: string[];
}) {
  const { t } = useI18n();
  const set = (patch: Partial<RingDraft>) => onChange({ ...draft, ...patch });
  return (
    <div className="space-y-3">
      <Field label={t.tournament.ringName} required>
        <input className="input" value={draft.name} placeholder={t.tournament.ringNameExample}
          onChange={e => set({ name: e.target.value })} data-autofocus />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t.tournament.ringMinAgeGroup}>
          <select className="input" value={draft.min_age_group} onChange={e => set({ min_age_group: e.target.value })}>
            <option value="">{t.tournament.ringAny}</option>
            {ageCodes.map(ag => <option key={ag} value={ag}>{ag}</option>)}
          </select>
        </Field>
        <Field label={t.tournament.ringMaxAgeGroup}>
          <select className="input" value={draft.max_age_group} onChange={e => set({ max_age_group: e.target.value })}>
            <option value="">{t.tournament.ringAny}</option>
            {ageCodes.map(ag => <option key={ag} value={ag}>{ag}</option>)}
          </select>
        </Field>
        <Field label={t.tournament.maxWeightKgLabel} className="col-span-2">
          <select className="input" value={draft.max_weight_kg} onChange={e => set({ max_weight_kg: e.target.value })}>
            <option value="">{t.tournament.ringAnyWeight}</option>
            {RING_WEIGHT_LIMITS.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
        </Field>
      </div>
    </div>
  );
}
